<template>
  <div :style="containerStyle" class="input-search-wrapper">
    <a-input
      v-model:value="inputValue"
      :class="inputClass"
      :style="inputStyle"
      v-bind="mergedAttrs"
      @change="handleChange"
      @pressEnter="handlePressEnter"
      @blur="handleBlur"
    >
      <template #prefix>
        <slot v-if="$slots.prefix" name="prefix" />
        <IconSearch v-else />
      </template>
      <template #suffix>
        <slot v-if="$slots.suffix" name="suffix" />
      </template>
    </a-input>
    <div v-if="loading" class="loading-indicator"></div>
  </div>
</template>

<script setup lang="ts">
import IconSearch from '@/components/icons.new/IconSearch.vue'
import { computed, useAttrs } from 'vue'

interface Props {
  modelValue?: string
  placeholder?: string
  disabled?: boolean
  size?: 'large' | 'middle' | 'small'
  width?: string
  height?: string
  enterButton?: boolean | string
  loading?: boolean
  borderless?: boolean
  noBackground?: boolean
  class?: string
  label?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '',
  disabled: false,
  size: 'middle',
  width: undefined,
  height: undefined,
  enterButton: false,
  loading: false,
  borderless: false,
  noBackground: false,
  class: '',
  label: undefined,
  ariaLabel: undefined,
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [e: Event]
  search: [value: string, e?: Event]
  pressEnter: [e: KeyboardEvent]
  blur: [e: FocusEvent]
}>()

const attrs = useAttrs()

const inputValue = computed({
  get: () => props.modelValue,
  set: (val: string) => {
    emit('update:modelValue', val)
  },
})

const inputClass = computed(() => {
  return `input-search-custom ${props.borderless && !props.noBackground ? 'tw:bg-s-d-s-dark-background-opacity-100' : ''} ${props.class}`
})

const containerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.width = props.width
  }
  return style
})

const inputStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.height) {
    style.height = props.height
  }
  return style
})

// Merge props với attrs, props có priority cao hơn
const mergedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...restAttrs } = attrs
  return {
    ...restAttrs,
    placeholder: props.placeholder,
    disabled: props.disabled,
    size: props.size,
    enterButton: props.enterButton,
    loading: props.loading,
    bordered: !props.borderless,
    'aria-label': props.ariaLabel || props.label || props.placeholder,
  }
})

const handleChange = (e: Event) => {
  emit('change', e)
}

const handleSearch = (value: string, e?: Event) => {
  emit('search', value, e)
}

const handlePressEnter = (e: KeyboardEvent) => {
  emit('pressEnter', e)
  // Also emit search event when Enter is pressed
  handleSearch(props.modelValue || '', e)
}

const handleBlur = (e: FocusEvent) => {
  emit('blur', e)
}
</script>

<style scoped lang="less">
.loading-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: var(--sds-color-background-brand-default, #bef975);
  animation: loading-slide 1.5s infinite ease-in-out;
}

@keyframes loading-slide {
  0% {
    left: 0;
    width: 0;
  }
  50% {
    left: 0;
    width: 100%;
  }
  100% {
    left: 100%;
    width: 0;
  }
}
</style>
